import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import Popover, { type PopoverProps } from '@mui/material/Popover';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { XIcon } from '@phosphor-icons/react/X';
import { t } from 'i18next';

export type FiltersPopoverProps = PopoverProps & {
  onClose: () => void;
  onApply: () => void;
  onClear: () => void;
  children: React.ReactNode;
  title: string;
  isApplyable?: boolean;
  isClearable?: boolean;
  'data-test'?: string;
};

export const FiltersPopover = ({
  onClose,
  onApply,
  onClear,
  title,
  children,
  isApplyable,
  isClearable,
  'data-test': dataTest = 'filters-popover',
  ...rest
}: FiltersPopoverProps) => {
  return (
    <Popover
      onClose={onClose}
      anchorOrigin={{
        vertical: 'bottom',
        horizontal: 'right',
      }}
      transformOrigin={{
        vertical: 'top',
        horizontal: 'right',
      }}
      {...rest}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', minWidth: 300 }}>
        <Stack
          direction="row"
          sx={{
            alignItems: 'center',
            justifyContent: 'space-between',
            paddingX: 1.5,
            paddingY: 1,
          }}
        >
          <Typography variant="h3" data-test={`${dataTest}-title`}>
            {title}
          </Typography>
          <IconButton aria-label="Close" data-test={`${dataTest}-close-button`} onClick={onClose} size="large">
            <XIcon />
          </IconButton>
        </Stack>

        <Divider />

        <Box
          sx={{
            paddingX: 1.5,
            paddingY: 1,
          }}
        >
          {children}
        </Box>

        <Stack
          direction="row"
          sx={{
            justifyContent: 'flex-end',
            paddingX: 1.5,
            paddingY: 1,
            gap: 1,
          }}
        >
          <Button
            data-test={`${dataTest}-apply-button`}
            color="primary"
            disabled={!isApplyable}
            onMouseDown={e => e.preventDefault()} // Stop Autocomplete from eating the click when input is focused
            onClick={() => {
              onApply();
            }}
            variant="contained"
          >
            {t('Apply')}
          </Button>

          <Button
            sx={{ color: 'text.secondary' }}
            data-test={`${dataTest}-clear-button`}
            disabled={!isClearable}
            onMouseDown={e => e.preventDefault()} // Stop Autocomplete from eating the click when input is focused
            onClick={() => {
              onClear();
            }}
            variant="text"
          >
            {t('Clear filters')}
          </Button>
        </Stack>
      </Box>
    </Popover>
  );
};
